/**
 * SITE UTILITIES (2026-10-07, Matt: "Add a typical residential gas meter (model it
 * accurately) to a new Site Utilities category … Also add an in-ground water meter in a
 * yard box"): the two service entrances every house plan shows.
 *
 * GAS METER SET (`gasMeter`) — the standard residential set against the house wall, as
 * the gas company installs it: the anodeless riser coming up out of the ground (its
 * yellow coated casing), the service shutoff (a plug valve with its square stop and
 * lock wing, open = in line with the pipe), the service regulator (the round diaphragm
 * bell facing out, its vent turned down), the riser carried up over the meter and down
 * into the meter's INLET hub on the left, a 250-class diaphragm meter (two-piece
 * aluminum case with its seam flange, swivel hubs on top, the index on the front behind
 * glass), and the house line rising off the OUTLET hub and turning into the wall. An
 * optional earthquake (seismic) shutoff sits on the outlet riser (per placement).
 *
 * WATER METER BOX (`waterMeterBox`) — the meter in its yard box, set flush with grade:
 * a concrete box (open bottom on a gravel bed, notches in the end walls where the
 * service passes), its lid with a cast-iron reading lid, pick holes and cast WATER
 * lettering; inside, the curb (meter) valve with its lock wing on the street side,
 * couplings, a 5/8" × 3/4" bronze meter with its register, and the customer shutoff
 * (ball valve, lever handle) on the house side. Local y runs street (y = 0) → house.
 * Everything but the lid is below grade: the body hangs from z = 0 down to −height.
 *
 * Both are built at their real NOMINAL size in inches and scaled to the type's width ×
 * depth × height (so a resized type stays one consistent piece). Each part carries its
 * own colour through `solidGroups` (galvanized pipe, aluminum meter, yellow riser coat,
 * concrete, bronze …) — unless the type picks a surface material, which then colours the
 * whole thing. Local frame = the object convention: x across the width centred on 0, y
 * from the anchor (y = 0: the wall for the gas meter, the street end for the box), z up.
 */
import { MeshBuilder } from '../../extrude'
import { tintMaterialId, type Pt2 } from '@aj/bim-kernel'
import type { SolidGroup } from '../opening/protos'

type V3 = [number, number, number]

/** gas meter set nominal size (inches) — width × depth off the wall × height */
export const GAS_METER_NOMINAL_IN = { w: 30, d: 14, h: 45 }
/** water meter box nominal size (inches) — the standard 13" × 24" × 12" deep box */
export const WATER_BOX_NOMINAL_IN = { w: 13, d: 24, h: 12 }
/** the box rim / lid sits this far proud of grade; the WATER letters a touch more (in) */
const BOX_RIM_IN = 0.25
const LETTER_TOP_IN = 0.31
/** the water box's top above its base, as a fraction of its height (objectZSpan) */
export const WATER_BOX_TOP_FRAC = LETTER_TOP_IN / WATER_BOX_NOMINAL_IN.h

const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const mul = (a: V3, k: number): V3 => [a[0] * k, a[1] * k, a[2] * k]
const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
const norm = (a: V3): V3 => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l] }

/**
 * A multi-colour mesh in NOMINAL INCHES: every emitted triangle goes to the builder of the
 * current colour, scaled into the type's feet. `finish` appends the parts to the proto's
 * one solid in colour order and returns the per-colour index ranges.
 */
class Parts {
  private byColor = new Map<string, MeshBuilder>()
  private cur!: MeshBuilder
  glass = new MeshBuilder()
  private toGlass = false
  constructor(private s: V3, private xo = 0) { this.color('#9a9ea0') }
  color(hex: string): this {
    let m = this.byColor.get(hex)
    if (!m) { m = new MeshBuilder(); this.byColor.set(hex, m) }
    this.cur = m
    this.toGlass = false
    return this
  }
  glassOn(): this { this.toGlass = true; return this }
  private P = (p: number[]): number[] => [(p[0] + this.xo) * this.s[0], p[1] * this.s[1], p[2] * this.s[2]]
  private N = (n: number[]): number[] => norm([n[0] / this.s[0], n[1] / this.s[1], n[2] / this.s[2]])
  triN(a: number[], b: number[], c: number[], na: number[], nb: number[], nc: number[]) {
    ;(this.toGlass ? this.glass : this.cur).addTriN(this.P(a), this.P(b), this.P(c), this.N(na), this.N(nb), this.N(nc))
  }
  tri(a: number[], b: number[], c: number[], n: number[]) { this.triN(a, b, c, n, n, n) }

  /** vertical prism over a convex CCW polygon */
  prism(poly: Pt2[], zA: number, zB: number) {
    const n = poly.length
    const bot = poly.map((q) => [q[0], q[1], zA]), top = poly.map((q) => [q[0], q[1], zB])
    for (let i = 2; i < n; i++) {
      this.tri(top[0], top[i - 1], top[i], [0, 0, 1])
      this.tri(bot[0], bot[i], bot[i - 1], [0, 0, -1])
    }
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      const nn = norm([poly[j][1] - poly[i][1], poly[i][0] - poly[j][0], 0])
      this.tri(bot[i], bot[j], top[j], nn)
      this.tri(bot[i], top[j], top[i], nn)
    }
  }
  box(x0: number, x1: number, y0: number, y1: number, zA: number, zB: number) {
    if (x1 - x0 < 1e-4 || y1 - y0 < 1e-4 || zB - zA < 1e-4) return
    this.prism([[x0, y0], [x1, y0], [x1, y1], [x0, y1]], zA, zB)
  }
  /** a flat bar of half-width t round the segment p→q (plan), over [zA, zB] */
  bar(p: Pt2, q: Pt2, t: number, zA: number, zB: number) {
    const dx = q[0] - p[0], dy = q[1] - p[1], L = Math.hypot(dx, dy) || 1
    const nx = (-dy / L) * t, ny = (dx / L) * t
    // extend the ends by t so joined strokes close their corners
    const ex = (dx / L) * t, ey = (dy / L) * t
    this.prism([[p[0] - nx - ex, p[1] - ny - ey], [q[0] - nx + ex, q[1] - ny + ey], [q[0] + nx + ex, q[1] + ny + ey], [p[0] + nx - ex, p[1] + ny - ey]], zA, zB)
  }
  /** a round tube swept along sample points with their tangents (parallel-transported
   *  frame, smooth sides), capped at both ends */
  tube(path: V3[], tans: V3[], r: number, segs = 12) {
    const t0 = norm(tans[0])
    let nrm = norm(Math.abs(t0[2]) < 0.9 ? cross(t0, [0, 0, 1]) : cross(t0, [1, 0, 0]))
    const rings: { p: V3[]; d: V3[] }[] = []
    for (let i = 0; i < path.length; i++) {
      const t = norm(tans[i])
      nrm = norm(sub(nrm, mul(t, dot(nrm, t))))
      const bb = cross(t, nrm)
      const p: V3[] = [], d: V3[] = []
      for (let k = 0; k < segs; k++) {
        const a = (Math.PI * 2 * k) / segs
        const dir = add(mul(nrm, Math.cos(a)), mul(bb, Math.sin(a)))
        d.push(dir); p.push(add(path[i], mul(dir, r)))
      }
      rings.push({ p, d })
    }
    for (let i = 0; i + 1 < rings.length; i++) {
      const R0 = rings[i], R1 = rings[i + 1]
      for (let k = 0; k < segs; k++) {
        const k1 = (k + 1) % segs
        this.triN(R0.p[k], R0.p[k1], R1.p[k1], R0.d[k], R0.d[k1], R1.d[k1])
        this.triN(R0.p[k], R1.p[k1], R1.p[k], R0.d[k], R1.d[k1], R1.d[k])
      }
    }
    const cap = (i: number, sign: number) => {
      const c = path[i], R = rings[i], n = mul(norm(tans[i]), sign)
      for (let k = 0; k < segs; k++) {
        const k1 = (k + 1) % segs
        if (sign < 0) this.tri(c, R.p[k1], R.p[k], n)
        else this.tri(c, R.p[k], R.p[k1], n)
      }
    }
    cap(0, -1); cap(path.length - 1, 1)
  }
  /** a straight capped cylinder from a to b */
  cyl(a: V3, b: V3, r: number, segs = 12) {
    const t = sub(b, a)
    if (Math.hypot(t[0], t[1], t[2]) < 1e-6) return
    this.tube([a, b], [t, t], r, segs)
  }
  /**
   * A pipe run through `pts`: straight lengths of radius r, and at each corner a FITTING
   * (a short-radius bend a size heavier — what a threaded elbow reads as).
   */
  pipe(pts: V3[], r: number, fit = r * 1.25, bendR = r * 2.2) {
    const segStart: V3[] = [pts[0]]
    const segEnd: V3[] = []
    for (let i = 1; i < pts.length - 1; i++) {
      const u1 = norm(sub(pts[i], pts[i - 1])), u2 = norm(sub(pts[i + 1], pts[i]))
      const th = Math.acos(Math.max(-1, Math.min(1, dot(u1, u2))))
      if (th < 1e-3) { segEnd.push(pts[i]); segStart.push(pts[i]); continue }
      const t = bendR * Math.tan(th / 2)
      const p1 = sub(pts[i], mul(u1, t)), p2 = add(pts[i], mul(u2, t))
      segEnd.push(p1); segStart.push(p2)
      const e = norm(sub(u2, mul(u1, dot(u1, u2))))
      const c = add(p1, mul(e, bendR))
      const e1 = mul(e, -1)
      const path: V3[] = [], tans: V3[] = []
      const n = 8
      // the fitting runs a little past the bend at both ends (its socket)
      const ext = r * 0.9
      path.push(sub(p1, mul(u1, ext))); tans.push(u1)
      for (let k = 0; k <= n; k++) {
        const f = (th * k) / n
        path.push(add(c, mul(add(mul(e1, Math.cos(f)), mul(u1, Math.sin(f))), bendR)))
        tans.push(add(mul(e1, -Math.sin(f)), mul(u1, Math.cos(f))))
      }
      path.push(add(p2, mul(u2, ext))); tans.push(u2)
      this.tube(path, tans, fit)
    }
    segEnd.push(pts[pts.length - 1])
    for (let i = 0; i < segStart.length; i++) this.cyl(segStart[i], segEnd[i], r)
  }
  finish(b: MeshBuilder, plain: boolean): SolidGroup[] {
    const groups: SolidGroup[] = []
    for (const [hex, m] of this.byColor) {
      if (!m.vertCount) continue
      const start = b.idx.length, base = b.vertCount
      // element-wise, never push(...big) — large arrays overflow a worker's call stack
      for (let i = 0; i < m.pos.length; i++) b.pos.push(m.pos[i])
      for (let i = 0; i < m.nrm.length; i++) b.nrm.push(m.nrm[i])
      for (const i of m.idx) b.idx.push(i + base)
      b.vertCount += m.vertCount
      groups.push({ start, count: m.idx.length, materialId: tintMaterialId(hex, 'default') })
    }
    return plain ? [] : groups
  }
}

/** a CCW rounded rectangle (inches) */
function roundRect(x0: number, y0: number, x1: number, y1: number, r: number, seg = 4): Pt2[] {
  r = Math.min(r, (x1 - x0) / 2, (y1 - y0) / 2)
  const out: Pt2[] = []
  const cs: [number, number, number][] = [[x1 - r, y1 - r, 0], [x0 + r, y1 - r, 90], [x0 + r, y0 + r, 180], [x1 - r, y0 + r, 270]]
  for (const [cx, cy, a0] of cs) for (let i = 0; i <= seg; i++) {
    const a = ((a0 + (90 * i) / seg) * Math.PI) / 180
    out.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r])
  }
  return out
}

const C = {
  meter: '#b6babb',       // aluminum case
  index: '#474c4f',       // index housing
  dial: '#ecebe4',
  ink: '#1d1f20',
  pipe: '#868b8d',        // galvanized / grey-painted steel
  riserCoat: '#d6b03c',   // the anodeless riser's yellow coated casing
  valve: '#64696b',
  regulator: '#8f989b',
  seismic: '#9ea4a6',
  concrete: '#aaa69d',
  lid: '#9a968d',
  iron: '#2a2c2e',
  gravel: '#8b867b',
  bronze: '#9b7c4e',
  copper: '#b5743a',
  register: '#2b2f33',
  lever: '#b13a2e',
}

// ---- gas meter set (inches; x is shifted +1 so the set is centred in its width) ----
const G = {
  xo: 1,
  xr: -9,            // the riser line
  ym: 7,             // the pipe line off the wall (the meter hubs' centre)
  r: 0.66,           // 1" steel pipe, 1.315" OD
  meter: { x0: -2, x1: 10, y0: 2.5, y1: 11.5, z0: 17, z1: 30 },
  hubIn: 0.5, hubOut: 7.5,
  hubTop: 32.7,
}

export function buildGasMeter(w: number, d: number, h: number, b: MeshBuilder, seismic: boolean, plain: boolean): { groups: SolidGroup[]; glass: MeshBuilder } {
  const N = GAS_METER_NOMINAL_IN
  const P = new Parts([w / N.w, d / N.d, h / N.h], G.xo)
  const { xr, ym, r } = G
  const M = G.meter

  // the anodeless riser: its coated casing out of the ground, the transition fitting
  P.color(C.riserCoat).cyl([xr, ym, 0], [xr, ym, 9], 0.95)
  P.color(C.valve).cyl([xr, ym, 9], [xr, ym, 10.6], 1.05, 6)
  P.color(C.pipe).cyl([xr, ym, 10.6], [xr, ym, 13.2], r)

  // the service shutoff — a plug valve: hex ends, round body, the square stop on the
  // front and the lock wing standing in line with the pipe (open)
  P.color(C.valve)
  P.cyl([xr, ym, 13], [xr, ym, 13.7], 1.2, 6)
  P.cyl([xr, ym, 13.6], [xr, ym, 15.4], 1.35)
  P.cyl([xr, ym, 15.3], [xr, ym, 16], 1.2, 6)
  P.box(xr - 0.5, xr + 0.5, ym + 1.2, ym + 2.3, 14.0, 15.0)            // square stop
  P.box(xr - 0.14, xr + 0.14, ym + 2.15, ym + 2.55, 13.2, 15.8)        // lock wing
  P.color(C.pipe).cyl([xr, ym, 15.9], [xr, ym, 18.2], r)

  // the service regulator — inline body, the diaphragm bell facing out on its neck,
  // the closing cap, and the vent turned down with its screen
  P.color(C.regulator)
  P.cyl([xr, ym, 18], [xr, ym, 18.8], 1.15, 6)
  P.cyl([xr, ym, 18.7], [xr, ym, 21.8], 1.45)
  P.cyl([xr, ym, 21.7], [xr, ym, 22.5], 1.15, 6)
  const zb = 20.6
  P.cyl([xr, ym, zb], [xr, ym + 1.7, zb], 1.1)                          // neck
  P.cyl([xr, ym + 1.6, zb], [xr, ym + 2.6, zb], 3.3, 20)                // lower spring case
  P.cyl([xr, ym + 2.55, zb], [xr, ym + 2.9, zb], 3.6, 20)               // flange / bolt ring
  P.cyl([xr, ym + 2.85, zb], [xr, ym + 3.7, zb], 3.2, 20)               // upper case
  P.cyl([xr, ym + 3.65, zb], [xr, ym + 4.4, zb], 2.1, 16)               // spring dome
  P.cyl([xr, ym + 4.35, zb], [xr, ym + 4.85, zb], 0.95, 8)              // closing cap
  P.pipe([[xr + 1.7, ym + 2.2, zb - 2.6], [xr + 1.7, ym + 2.2, 15.4]], 0.45)  // vent
  P.cyl([xr + 1.7, ym + 2.2, 14.6], [xr + 1.7, ym + 2.2, 15.5], 0.75, 10)     // vent screen

  // the riser carried up over the meter and down into the INLET hub (left)
  P.color(C.pipe).pipe([[xr, ym, 22.4], [xr, ym, 35.4], [G.hubIn, ym, 35.4], [G.hubIn, ym, G.hubTop]], r)

  // the meter — a 250-class diaphragm meter: lower case, the seam flange, upper case,
  // the top cover, the swivel hubs with their nuts
  P.color(C.meter)
  P.prism(roundRect(M.x0, M.y0, M.x1, M.y1, 1.6), M.z0, 23.2)
  P.prism(roundRect(M.x0 - 0.35, M.y0 - 0.35, M.x1 + 0.35, M.y1 + 0.35, 1.8), 23.1, 24.1)
  P.prism(roundRect(M.x0, M.y0, M.x1, M.y1, 1.6), 24, 29.5)
  P.prism(roundRect(M.x0 + 0.5, M.y0 + 0.5, M.x1 - 0.5, M.y1 - 0.5, 1.2), 29.4, M.z1)
  for (const hx of [G.hubIn, G.hubOut]) {
    P.cyl([hx, ym, M.z1 - 0.1], [hx, ym, 31.5], 1.1)
    P.cyl([hx, ym, 31.4], [hx, ym, G.hubTop], 1.3, 6)
  }
  // the index on the front: its housing, the dial face, four dial hands, the glass
  const xm = (M.x0 + M.x1) / 2
  P.color(C.index)
  P.box(xm - 2.9, xm + 2.9, M.y1 - 0.1, M.y1 + 0.9, 23.9, 28.8)
  P.box(xm - 2.5, xm + 2.5, M.y1 + 0.9, M.y1 + 1.15, 24.3, 24.6)       // window frame
  P.box(xm - 2.5, xm + 2.5, M.y1 + 0.9, M.y1 + 1.15, 28.1, 28.4)
  P.box(xm - 2.5, xm - 2.2, M.y1 + 0.9, M.y1 + 1.15, 24.6, 28.1)
  P.box(xm + 2.2, xm + 2.5, M.y1 + 0.9, M.y1 + 1.15, 24.6, 28.1)
  P.color(C.dial).box(xm - 2.2, xm + 2.2, M.y1 + 0.9, M.y1 + 0.95, 24.6, 28.1)
  P.color(C.ink)
  for (let i = 0; i < 4; i++) {
    const cx = xm - 1.5 + i, cz = 26.9
    P.cyl([cx, M.y1 + 0.95, cz], [cx, M.y1 + 0.99, cz], 0.42, 12)
    P.box(cx - 0.04, cx + 0.04, M.y1 + 0.99, M.y1 + 1.02, cz, cz + 0.38)
  }
  P.box(xm - 1.2, xm + 1.2, M.y1 + 0.95, M.y1 + 0.99, 25.0, 25.5)       // odometer strip
  P.glassOn().box(xm - 2.2, xm + 2.2, M.y1 + 1.06, M.y1 + 1.1, 24.6, 28.1)

  // the house line: up off the OUTLET hub (through the earthquake valve when fitted)
  // and turned into the wall
  const zTop = seismic ? 43.9 : 38
  P.color(C.pipe).pipe([[G.hubOut, ym, G.hubTop - 0.2], [G.hubOut, ym, zTop], [G.hubOut, 0, zTop]], r)
  if (seismic) {
    P.color(C.seismic)
    P.cyl([G.hubOut, ym, 35.4], [G.hubOut, ym, 36.1], 1.15, 6)
    P.cyl([G.hubOut, ym, 36], [G.hubOut, ym, 39.6], 1.55, 16)
    P.cyl([G.hubOut, ym, 39.5], [G.hubOut, ym, 40.4], 1.75, 16)
    P.color(C.ink).cyl([G.hubOut, ym, 40.3], [G.hubOut, ym, 40.9], 0.6, 10)   // reset knob
  }
  // the escutcheon where the house line enters the wall
  P.color(C.pipe).cyl([G.hubOut, 0, zTop], [G.hubOut, 0.25, zTop], 1.4, 14)

  return { groups: P.finish(b, plain), glass: P.glass }
}

/** the gas meter set in plan: the meter case and its index, the regulator bell, the
 *  riser, the run over the meter into the inlet, and the house line into the wall */
export function gasMeterPlanSymbol(w: number, d: number, seismic: boolean): { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] {
  const N = GAS_METER_NOMINAL_IN
  const sx = w / N.w, sy = d / N.d
  const T = (q: Pt2): Pt2 => [(q[0] + G.xo) * sx, q[1] * sy]
  const M = G.meter, { xr, ym } = G
  const xm = (M.x0 + M.x1) / 2
  const rect = (x0: number, y0: number, x1: number, y1: number) => ({ pts: [T([x0, y0]), T([x1, y0]), T([x1, y1]), T([x0, y1])], closed: true })
  const circ = (cx: number, cy: number, r: number, n = 14) => ({
    pts: Array.from({ length: n }, (_, i) => T([cx + Math.cos((Math.PI * 2 * i) / n) * r, cy + Math.sin((Math.PI * 2 * i) / n) * r])),
    closed: true,
  })
  const out: { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] = [
    { pts: roundRect(M.x0, M.y0, M.x1, M.y1, 1.6).map(T), closed: true },  // meter case
    rect(xm - 2.9, M.y1, xm + 2.9, M.y1 + 1.15),                           // index
    rect(xr - 3.6, ym + 1.6, xr + 3.6, ym + 4.4),                          // regulator bell
    circ(xr, ym, 1.45),                                                     // riser / reg body
    { pts: [T([xr, ym]), T([G.hubIn, ym])] },                               // run to the inlet
    { pts: [T([G.hubOut, ym]), T([G.hubOut, 0])] },                         // house line
    circ(G.hubIn, ym, 1.3, 6), circ(G.hubOut, ym, 1.3, 6),                  // hubs
  ]
  if (seismic) out.push(circ(G.hubOut, ym, 1.75))
  return out
}

// ---- water meter box (inches; local y = street → house, the box hangs below grade) ----
const WB = { wall: 1.5, lidT: 2, ledge: 0.5 }

/** the cast WATER lettering, as stroke segments in a 1" × 1.6" letter cell */
const LETTERS: Record<string, [number, number, number, number][]> = {
  W: [[0, 1.6, 0.25, 0], [0.25, 0, 0.5, 0.9], [0.5, 0.9, 0.75, 0], [0.75, 0, 1, 1.6]],
  A: [[0, 0, 0.5, 1.6], [0.5, 1.6, 1, 0], [0.22, 0.62, 0.78, 0.62]],
  T: [[0, 1.6, 1, 1.6], [0.5, 1.6, 0.5, 0]],
  E: [[0, 0, 0, 1.6], [0, 1.6, 0.9, 1.6], [0, 0.8, 0.7, 0.8], [0, 0, 0.9, 0]],
  R: [[0, 0, 0, 1.6], [0, 1.6, 0.72, 1.6], [0.72, 1.6, 0.92, 1.25], [0.92, 1.25, 0.72, 0.85], [0.72, 0.85, 0, 0.85], [0.4, 0.85, 0.95, 0]],
}

export function buildWaterMeterBox(w: number, d: number, h: number, b: MeshBuilder, plain: boolean): { groups: SolidGroup[] } {
  const N = WATER_BOX_NOMINAL_IN
  const P = new Parts([w / N.w, d / N.d, h / N.h])
  const W = N.w, D = N.d, H = N.h
  const t = WB.wall, top = BOX_RIM_IN
  const zp = -H + 5.5            // the service line's centreline
  const notch = 2.3              // half-width of the pipe notches in the end walls

  // the box: two long walls, the end walls split round the pipe notches, the lid ledge
  P.color(C.concrete)
  P.box(-W / 2, -W / 2 + t, 0, D, -H, top)
  P.box(W / 2 - t, W / 2, 0, D, -H, top)
  for (const [y0, y1] of [[0, t], [D - t, D]] as const) {
    P.box(-W / 2 + t, -notch, y0, y1, -H, top)
    P.box(notch, W / 2 - t, y0, y1, -H, top)
    P.box(-notch, notch, y0, y1, zp + 1.9, top)
  }
  const li = WB.ledge
  const lz = top - WB.lidT
  P.box(-W / 2 + t, -W / 2 + t + li, t, D - t, lz - 0.6, lz)
  P.box(W / 2 - t - li, W / 2 - t, t, D - t, lz - 0.6, lz)
  P.box(-W / 2 + t + li, W / 2 - t - li, t, t + li, lz - 0.6, lz)
  P.box(-W / 2 + t + li, W / 2 - t - li, D - t - li, D - t, lz - 0.6, lz)

  // the gravel bed in the open bottom
  P.color(C.gravel).box(-W / 2 + t, W / 2 - t, t, D - t, -H, -H + 2)

  // the lid, flush with the rim — with its reading lid, pick holes and WATER
  const gap = 0.12
  P.color(C.lid).box(-W / 2 + t + gap, W / 2 - t - gap, t + gap, D - t - gap, lz, top)
  const ry = 12.15
  P.color(C.iron)
  P.box(-2.3, 2.3, ry - 3, ry + 3, top - 0.05, top + 0.05)                 // reading lid
  P.box(-0.25, 0.25, ry + 2.2, ry + 2.7, top + 0.05, LETTER_TOP_IN)           // its lift slot rim
  for (const py of [t + 1.4, D - t - 1.4]) P.box(-0.6, 0.6, py - 0.2, py + 0.2, top - 0.02, top + 0.02)  // pick holes
  P.color(C.lid)
  const word = 'WATER', cell = 1.0, pitch = 1.35, sh = 1.6 / 1.6
  const x0 = -((word.length - 1) * pitch + cell) / 2, yb = 4.2
  for (let i = 0; i < word.length; i++) {
    for (const [ax, ay, bx, by] of LETTERS[word[i]]) {
      P.bar([x0 + i * pitch + ax * cell, yb + ay * sh], [x0 + i * pitch + bx * cell, yb + by * sh], 0.11, top - 0.02, LETTER_TOP_IN)
    }
  }

  // the service: copper in from the street, the curb valve (lock wing), couplings, the
  // bronze meter and its register, the customer ball valve, copper on to the house
  const Y = (y: number): V3 => [0, y, zp]
  P.color(C.copper)
  P.cyl(Y(0), Y(4.1), 0.44)
  P.cyl(Y(19.5), Y(D), 0.44)
  P.color(C.bronze)
  P.cyl(Y(4), Y(4.7), 1.0, 6)                                            // curb valve
  P.cyl(Y(4.6), Y(7), 0.95)
  P.cyl([0, 5.8, zp], [0, 5.8, zp + 1.5], 0.55)                           // its stem
  P.box(-0.15, 0.15, 5.0, 6.6, zp + 1.4, zp + 2.6)                       // lock wing
  P.cyl(Y(6.9), Y(8.4), 1.15, 6)                                         // coupling nut
  P.cyl(Y(8.4), Y(8.9), 0.85, 6)                                         // meter spud
  P.cyl(Y(8.8), Y(15.5), 1.45, 16)                                       // meter body
  P.cyl([0, ry, zp], [0, ry, zp + 1.5], 1.7, 16)                         // register boss
  P.cyl(Y(15.4), Y(15.9), 0.85, 6)
  P.cyl(Y(15.9), Y(17.4), 1.15, 6)                                       // coupling nut
  P.cyl(Y(17.3), Y(19.6), 0.9)                                           // ball valve
  P.color(C.register)
  P.cyl([0, ry, zp + 1.45], [0, ry, zp + 2.9], 1.95, 18)                 // register
  P.cyl([0, ry, zp + 2.85], [0, ry, zp + 3.25], 2.05, 18)                // its lid
  P.color(C.lever)
  P.box(-0.3, 0.3, 17.6, 21.2, zp + 1.05, zp + 1.35)                     // ball-valve lever
  P.cyl([0, 18.45, zp], [0, 18.45, zp + 1.2], 0.3, 8)

  return { groups: P.finish(b, plain) }
}

/** the yard box in plan: the box, its lid, the reading lid, and the service line running
 *  through it (dashed — it is under the lid) */
export function waterMeterBoxPlanSymbol(w: number, d: number): { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] {
  const N = WATER_BOX_NOMINAL_IN
  const sx = w / N.w, sy = d / N.d
  const T = (q: Pt2): Pt2 => [q[0] * sx, q[1] * sy]
  const rect = (x0: number, y0: number, x1: number, y1: number) => ({ pts: [T([x0, y0]), T([x1, y0]), T([x1, y1]), T([x0, y1])], closed: true })
  const t = WB.wall
  return [
    rect(-N.w / 2, 0, N.w / 2, N.d),
    rect(-N.w / 2 + t, t, N.w / 2 - t, N.d - t),
    rect(-2.3, 12.15 - 3, 2.3, 12.15 + 3),
    { pts: [T([0, 0]), T([0, 12.15 - 3])], dashed: true },
    { pts: [T([0, 12.15 + 3]), T([0, N.d])], dashed: true },
  ]
}
